import { FormikProps } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { FiPhone, FiShoppingBag, FiShoppingCart } from "react-icons/fi";
import { LuMapPin, LuUser } from "react-icons/lu";
import { toast } from "react-toastify";
import { calculateCost } from "../../apis/services/create-service";
import Button from "../Button";
import Input from "../Input";
import Spinner from "../Spinner";
import { MainFormType } from "./CreateServiceModal";
import { formatDateForInput } from "../../utils/helpers";
import { MdOutlineSchedule } from "react-icons/md";

interface OrderSummaryProps {
  form: FormikProps<MainFormType>;
  onSubmit: () => void;
}

const OrderSummary = ({ form, onSubmit }: OrderSummaryProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const formPromoCode = form.values.promoCode;
  const service = form.values[form.values.serviceType!];
  const isDeliveriesAvailable = Number(form?.values?.recipientDetails?.isDeliveriesAvailable)

  const [promoCode, setPromoCode] = useState(formPromoCode || "");
  const [isCalculatingCost, setIsCalculatingCost] = useState(false);

  const getFields = useCallback(() => {
    switch (form.values.serviceType) {
      case "package_pickup_meta": {
        return (
          <div className="md:grid md:grid-cols-2 flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-4">
              <p className="text-lg font-medium">Pickup information</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  < LuUser size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Name: </p>
                </div>
                <p>{`${form.values.package_pickup_meta.orderInformation.firstName} ${form.values.package_pickup_meta.orderInformation.lastName} `}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FiPhone size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Phone: </p>
                </div>
                <p>
                  {" "}
                  {form.values.package_pickup_meta.orderInformation.phone.includes(
                    "+"
                  )
                    ? ""
                    : "+"}
                  {form.values.package_pickup_meta.orderInformation.phone}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Address: </p>
                </div>
                <p>
                  {form.values.package_pickup_meta.pickupInformation.address}
                </p>
              </div>
              {form.values.scheduleTime && <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <MdOutlineSchedule size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Schedule time: </p>
                </div>
                <p>
                  {formatDateForInput(form.values.scheduleTime.toString()).replace('T', " ")}
                </p>
              </div>}

            </div>

            <div className="flex flex-col gap-4">
              <p className="text-lg font-medium">Recipient information</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <LuUser size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Name: </p>
                </div>
                <p>{form.values.recipientDetails.name}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FiPhone size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Phone: </p>
                </div>
                <p>
                  {form.values.recipientDetails.phone.includes("+") ? "" : "+"}
                  {form.values.recipientDetails.phone}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Address: </p>
                </div>
                <p>
                  {form.values.package_pickup_meta.dropoffInformation.address}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Distance: </p>
                </div>
                <p>{form.values.serviceCost?.distance?.toFixed(2)} mi</p>
              </div>
            </div>
          </div>
        );
      }
      case "curbside_pickup_meta": {
        return (
          <div className="md:grid md:grid-cols-2 flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FiShoppingBag size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Order no#: </p>
                </div>
                <p>
                  {
                    form.values.curbside_pickup_meta.orderInformation
                      .orderNumber
                  }
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FiShoppingCart size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Store Name: </p>
                </div>
                <p>
                  {form.values.curbside_pickup_meta.orderInformation.storeName}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Store Address: </p>
                </div>
                <p>
                  {form.values.curbside_pickup_meta.pickupInformation.address}
                </p>
              </div>
              {form.values.scheduleTime && <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <MdOutlineSchedule size={24} className="text-primary" />
                  <p className='w-16 md:w-20'>Schedule time: </p>
                </div>
                <p>
                  {formatDateForInput(form.values.scheduleTime.toString()).replace('T', " ")}
                </p>
              </div>}
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <LuUser size={24} className="text-primary" />
                  <p className='w-20'>Name: </p>
                </div>
                <p>{form.values.recipientDetails.name}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FiPhone size={24} className="text-primary" />
                  <p className='w-20'>Phone: </p>
                </div>
                <p>
                  {form.values.package_pickup_meta.orderInformation.phone.includes(
                    "+"
                  )
                    ? ""
                    : "+"}
                  {form.values.recipientDetails.phone}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-20'>Delivery Address: </p>
                </div>
                <p>
                  {form.values.curbside_pickup_meta.dropoffInformation.address}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2">
                  <LuMapPin size={24} className="text-primary" />
                  <p className='w-20'>Distance: </p>
                </div>
                <p>{form.values.serviceCost?.distance?.toFixed(2)} mi</p>
              </div>
            </div>
          </div>
        );
      }
      case "instore_pickup_meta": {
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">

            {/* LEFT COLUMN */}
            <div className="flex flex-col gap-4">

              <div className="flex items-start gap-3">
                <FiShoppingBag size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Order no#</p>
                <p className="w-[70%] text-muted-foreground break-all">
                  {form.values.instore_pickup_meta.orderInformation.orderNumber}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <FiShoppingCart size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Store name</p>
                <p className="w-[70%] text-muted-foreground break-words">
                  {form.values.instore_pickup_meta.orderInformation.storeName}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <LuMapPin size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Store address</p>
                <p className="w-[70%] text-muted-foreground break-words">
                  {form.values.instore_pickup_meta.pickupInformation.address}
                </p>
              </div>

              {form.values.scheduleTime && (
                <div className="flex items-start gap-3">
                  <MdOutlineSchedule size={20} className="text-primary mt-1 shrink-0" />
                  <p className="w-[30%] shrink-0 font-medium">Schedule time</p>
                  <p className="w-[70%] text-muted-foreground break-words">
                    {formatDateForInput(form.values.scheduleTime.toString()).replace("T", " ")}
                  </p>
                </div>
              )}

            </div>

            {/* RIGHT COLUMN */}
            <div className="flex flex-col gap-4">

              <div className="flex items-start gap-3">
                <LuUser size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Name</p>
                <p className="w-[70%] text-muted-foreground break-words">
                  {form.values.recipientDetails.name}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <FiPhone size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Phone</p>
                <p className="w-[70%] text-muted-foreground break-all">
                  {form.values.recipientDetails.phone.includes("+") ? "" : "+"}
                  {form.values.recipientDetails.phone}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <LuMapPin size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Delivery address</p>
                <p className="w-[70%] text-muted-foreground break-words">
                  {form.values.instore_pickup_meta.dropoffInformation.address}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <LuMapPin size={20} className="text-primary mt-1 shrink-0" />
                <p className="w-[30%] shrink-0 font-medium">Distance</p>
                <p className="w-[70%] text-muted-foreground">
                  {form.values.serviceCost?.distance?.toFixed(2)} mi
                </p>
              </div>

            </div>

          </div>
        );
      }
    }
  }, [form.values.serviceType, form?.values?.serviceCost?.distance]);

  useEffect(() => {
    const getCalculatedCost = async () => {
      setIsCalculatingCost(true);
      const res = await calculateCost({
        pickupLat: service.pickupInformation.lat!,
        pickupLon: service.pickupInformation.lng!,
        deliveryLat: service.dropoffInformation.lat!,
        deliveryLon: service.dropoffInformation.lng!,
        maxDistance: form.values.maxDistance!,
        serviceType: form.values.serviceType!,
        promoCode: form.values.promoCode || undefined,
      });
      if (res?.success) {
        form.setFieldValue("serviceCost", {
          ...res?.data?.serviceCost,
          distance: res?.data?.distance,
        });
        if (res?.data?.promoMessage) {
          toast.info(res?.data?.promoMessage);
        }
      }
      setIsCalculatingCost(false);
    };
    if (service && form.values.serviceType) {
      getCalculatedCost();
    }
  }, [
    service,
    form.values.serviceType,
    form.values.promoCode,
    form.values.maxDistance,
  ]);

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
        onSubmit()
      }, 300)
    }} className="flex flex-col gap-4">
      <p className="text-lg font-semibold text-center">Order summary</p>
      <div className="flex gap-4 flex-col">
        <div className="border border-primary rounded-lg p-4">
          {getFields()}
        </div>
        {!isDeliveriesAvailable && <div className="md:grid md:grid-cols-2 flex flex-col gap-4">
          <div className="border flex flex-col gap-4 border-primary rounded-lg p-4">
            <p>Have a promo code? Apply now for amazing discounts! </p>
            <div className="flex items-center gap-2 w-full">
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="h-10 w-full px-2"
                placeholder="Enter promo code"
                onKeyDown={(event) => {
                  if (event.keyCode === 13) {
                    event.preventDefault();
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (promoCode) {
                    form.setFieldValue("promoCode", promoCode);
                  }
                }}
                disabled={promoCode === ""}
                isLoading={isCalculatingCost}
                type="button"
                className="h-10 w-24 md:w-32 shrink-0"
              >
                Apply
              </Button>
            </div>
          </div>
          <div className="border flex flex-col gap-4 border-primary rounded-lg p-4">
            {form.values.serviceCost === null && isCalculatingCost ? (
              <div>
                <Spinner />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p>Fees</p>
                  <p>$ {form.values.serviceCost?.fee}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Delivery charges</p>
                  <p>$ {form.values.serviceCost?.deliveryCharges}</p>
                </div>
                <div className="flex pb-2 border-b border-gray-400 text-xs items-center justify-center">
                  <p>Delivery by SWYVERY</p>
                </div>

                <div className="flex items-center justify-between">
                  <p className="font-medium">Total</p>
                  <p className="font-semibold text-primary">
                    $ {form.values.serviceCost?.userTotal}
                  </p>
                </div>
                {Number(form.values.serviceCost?.discount) > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="font-medium">Discount</p>
                      <p className="font-semibold text-gray-500">
                        - $ {form.values.serviceCost?.discount}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-medium">Discounted total</p>
                      <p className="font-semibold text-primary">
                        $ {form.values.serviceCost?.discountedUserTotal}
                      </p>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>}
        <div className="flex flex-col pb-4 border-b border-primary">
          <p className="font-medium">Review package guidelines</p>
          <p className="font-medium">
            For a smooth and reliable delivery experience, make sure your package is:
          </p>
          <ul className="list-disc pl-6">
            <li>45 lbs or less (recommended) </li>
            <li>Securely sealed and ready for pickup </li>
          </ul>
        </div>
        <div className="flex flex-col pb-4 border-b border-primary">
          <p className="font-medium">
            The following items are not allowed:
          </p>
          <ul className="list-disc pl-6">
            <li>Illegal items or stolen goods </li>
            <li>Controlled substances or drugs </li>
            <li>Firearms, weapons, or ammunition </li>
            <li>Hazardous or dangerous materials (e.g., explosives, flammable substances) </li>
          </ul>
          <p>Some items may be restricted or require approval, including alcohol, medications, and high-value goods.</p>
          <a
            href="https://swyvery.com/prohibited-items-list"
            target="_blank"
            className="text-primary underline"
          >
            See all prohibited Items
          </a>
        </div>

        <div className="flex flex-col pb-4 ">
          <p className="font-medium">
            Additional notes
          </p>
          <ul className="list-disc pl-6">
            <li>For shipments over $200 in value, we recommend arranging third-party insurance, as Swyvery does not currently provide package insurance coverage  </li>
            <li>Heavier or bulky items may be declined based on driver availability  </li>
            <li>Firearms, weapons, or ammunition </li>
            <li>By placing an order, you agree to our <a
              className="text-primary underline"
              target="_blank"
              href="https://swyvery.com/terms-and-conditions"
            >
              Terms and Conditions.
            </a></li>
          </ul>

        </div>
      </div>
      <div className='flex justify-center'>
        <Button className="w-full md:w-32 min-h-0 h-10 md:h-10" isLoading={isLoading} type="submit">Continue</Button>
      </div>
    </form>
  );
};

export default OrderSummary;
